// ─────────────────────────────────────────────────────────────────────────────
// EventPerformancePanel
//
// "Your trading around events" — sits at the bottom of the Market page.
// Reads cached US high-impact events from economic_events_cache and the
// user's trades, computes performance comparisons via useEventPerformance.
//
// Two views:
//   1. Headline — event-day vs non-event-day win rate and avg P&L
//   2. By event class — FOMC, CPI, NFP, etc. breakdown
//
// Sample-size honesty:
//   - <3 trades in a bucket: bucket is hidden entirely (would be pure noise)
//   - 3-9 trades: shown with "small sample" caveat
//   - 10+ trades: shown straight
//
// Empty states:
//   - No trades in window: explain the empty state with the chosen filters
//   - Trades exist but none on event days: surface this as discipline data
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from "react";
import { Card } from "../../components/ui/Card";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { C } from "../../lib/colors";
import { fmt$, fmtPct } from "../../lib/format";
import { useAllTradesForAnalytics } from "../../hooks/useAllTradesForAnalytics";
import { useEventPerformance } from "../../hooks/useEventPerformance";

const WINDOW_OPTIONS = [
  { label: "3M",  days: 90  },
  { label: "6M",  days: 180 },
  { label: "1Y",  days: 365 },
];

export function EventPerformancePanel({ accounts = [] }) {
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [daysBack, setDaysBack] = useState(180);

  // Fetch ALL trades across accounts (bypassing useSupabase's account
  // scoping) so the "All Accounts" view is actually all accounts.
  const { trades: allTrades, loading: tradesLoading, error: tradesError } =
    useAllTradesForAnalytics(daysBack);

  const {
    loading: eventsLoading, error: eventsError, hasData, hasEventDayTrades,
    headline, byClass,
  } = useEventPerformance(allTrades, selectedAccountId || null, daysBack);

  // Combined loading/error states for the UI.
  const loading = tradesLoading || eventsLoading;
  const error = tradesError || eventsError;

  return (
    <Card style={{ marginBottom: 16 }}>
      <SectionTitle>
        Your Trading Around Events
        <span style={{ fontSize: 11, color: C.muted, fontWeight: 400, fontStyle: "italic" }}>
          High-impact US events only
        </span>
      </SectionTitle>

      {/* Toolbar: account + time window */}
      <div style={{
        display: "flex", gap: 12, marginBottom: 16, alignItems: "center",
        flexWrap: "wrap",
      }}>
        {/* Account selector */}
        {accounts.length > 0 && (
          <div style={{ flex: "1 1 220px", minWidth: 180 }}>
            <label style={{ fontSize: 11, color: C.muted, marginBottom: 4, display: "block" }}>
              Account
            </label>
            <select
              value={selectedAccountId}
              onChange={e => setSelectedAccountId(e.target.value)}
              style={{
                width: "100%", padding: "8px 10px",
                background: "#1a1d2e", border: `1px solid ${C.border}`,
                borderRadius: 6, color: C.text, fontSize: 12,
              }}
            >
              <option value="">All Accounts</option>
              {accounts.map(acc => (
                <option key={acc.id} value={acc.id}>
                  {acc.name} — {acc.account_type?.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Time window pill buttons */}
        <div>
          <label style={{ fontSize: 11, color: C.muted, marginBottom: 4, display: "block" }}>
            Look back
          </label>
          <div style={{ display: "flex", gap: 4 }}>
            {WINDOW_OPTIONS.map(opt => {
              const active = daysBack === opt.days;
              return (
                <button
                  key={opt.days}
                  onClick={() => setDaysBack(opt.days)}
                  style={{
                    padding: "8px 14px",
                    background: active ? C.blue + "20" : "transparent",
                    border: `1px solid ${active ? C.blue : C.border}`,
                    borderRadius: 6,
                    color: active ? C.blue : C.sub,
                    fontWeight: active ? 700 : 500,
                    cursor: "pointer", fontSize: 12,
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Body */}
      {loading && (
        <EmptyState
          icon="⟳"
          title="Loading event data..."
          color={C.yellow}
        />
      )}

      {error && (
        <EmptyState
          icon="⚠"
          title="Couldn't load event data"
          desc={error}
          color={C.red}
        />
      )}

      {!loading && !error && !hasData && (
        <EmptyState
          icon="📭"
          title="No trades in this window"
          desc={`No completed trades for the selected account in the last ${daysBack} days. Try a longer window or a different account.`}
          color={C.muted}
        />
      )}

      {!loading && !error && hasData && !hasEventDayTrades && (
        <EmptyState
          icon="🛡️"
          title="You haven't traded on high-impact event days"
          desc="Either you avoid event-day volatility (a defensible strategy) or the events haven't lined up with your trading days. There's nothing to compare yet."
          color={C.green}
        />
      )}

      {!loading && !error && hasEventDayTrades && (
        <>
          <Headline headline={headline} />
          <ClassBreakdown byClass={byClass} />
        </>
      )}
    </Card>
  );
}

// ── Headline: event-day vs non-event-day ───────────────────────────────────

function Headline({ headline }) {
  const { eventDay, nonEventDay } = headline;
  const diff = (eventDay.winRate ?? 0) - (nonEventDay.winRate ?? 0);
  const eventDayWorse = diff < -0.05;  // 5pp worse
  const eventDayBetter = diff > 0.05;

  const verdict = eventDayWorse
    ? { color: C.red,    text: "You trade WORSE on event days" }
    : eventDayBetter
    ? { color: C.green,  text: "You trade BETTER on event days" }
    : { color: C.muted,  text: "Roughly similar on event days" };

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{
        fontSize: 13, fontWeight: 700, color: verdict.color, marginBottom: 10,
      }}>
        {verdict.text}
      </div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 10,
      }}>
        <StatBox
          label="Event days"
          stats={eventDay}
          color={C.yellow}
          accent
        />
        <StatBox
          label="Other days"
          stats={nonEventDay}
          color={C.blue}
        />
      </div>
      {(eventDay.trades < 10 || nonEventDay.trades < 10) && (
        <div style={{
          fontSize: 11, color: C.muted, marginTop: 8, fontStyle: "italic",
        }}>
          Small sample — interpret with caution. More data builds confidence.
        </div>
      )}
    </div>
  );
}

function StatBox({ label, stats, color, accent }) {
  return (
    <div style={{
      padding: "12px 14px",
      background: accent ? color + "08" : "transparent",
      border: `1px solid ${accent ? color + "30" : C.border}`,
      borderRadius: 8,
    }}>
      <div style={{
        fontSize: 10, fontWeight: 700, color: C.muted,
        textTransform: "uppercase", letterSpacing: "0.08em",
      }}>{label}</div>
      <div style={{
        fontSize: 22, fontWeight: 800, color, marginTop: 4,
        fontFamily: "monospace",
      }}>
        {stats.winRate !== null ? fmtPct(stats.winRate) : "—"}
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
        win rate · {stats.trades} trade{stats.trades !== 1 ? "s" : ""}
      </div>
      <div style={{
        fontSize: 12, marginTop: 8,
        color: stats.totalPnl >= 0 ? C.green : C.red,
        fontFamily: "monospace",
      }}>
        {fmt$(stats.totalPnl)} total · {stats.avgPnl !== null ? fmt$(stats.avgPnl) : "—"} avg
      </div>
    </div>
  );
}

// ── Class breakdown ────────────────────────────────────────────────────────

function ClassBreakdown({ byClass }) {
  if (byClass.length === 0) {
    return (
      <div style={{
        fontSize: 12, color: C.muted, fontStyle: "italic",
        padding: "12px 0", borderTop: `1px solid ${C.border}`,
      }}>
        Not enough trades in any event class to break out separately
        (minimum 3 trades per class).
      </div>
    );
  }

  return (
    <div>
      <div style={{
        fontSize: 11, fontWeight: 700, color: C.muted,
        textTransform: "uppercase", letterSpacing: "0.08em",
        marginBottom: 8, paddingTop: 14,
        borderTop: `1px solid ${C.border}`,
      }}>
        Breakdown by event class
      </div>
      {byClass.map((b, i) => <ClassRow key={b.class} bucket={b} isLast={i === byClass.length - 1} />)}
    </div>
  );
}

function ClassRow({ bucket, isLast }) {
  const wrColor = bucket.winRate >= 0.5 ? C.green : bucket.winRate >= 0.4 ? C.yellow : C.red;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "10px 0",
      borderBottom: isLast ? "none" : `1px solid ${C.border}20`,
    }}>
      <div style={{ flex: "0 0 110px", fontSize: 13, fontWeight: 700, color: C.text }}>
        {bucket.class}
      </div>
      <div style={{ flex: "0 0 70px", fontSize: 14, fontWeight: 700, color: wrColor, fontFamily: "monospace" }}>
        {fmtPct(bucket.winRate)}
      </div>
      <div style={{ flex: "0 0 90px", fontSize: 11, color: C.muted }}>
        {bucket.trades} trade{bucket.trades !== 1 ? "s" : ""}
        {bucket.lowSample && (
          <span style={{ color: C.yellow, marginLeft: 4 }} title="Small sample size — interpret with caution">⚠</span>
        )}
      </div>
      <div style={{
        flex: 1, textAlign: "right", fontSize: 12,
        color: bucket.totalPnl >= 0 ? C.green : C.red,
        fontFamily: "monospace",
      }}>
        {fmt$(bucket.totalPnl)}
      </div>
    </div>
  );
}

// ── Generic empty state ────────────────────────────────────────────────────

function EmptyState({ icon, title, desc, color }) {
  return (
    <div style={{
      textAlign: "center", padding: "28px 16px",
      background: color + "08",
      border: `1px solid ${color}20`,
      borderRadius: 8,
    }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color }}>{title}</div>
      {desc && (
        <div style={{
          fontSize: 12, color: C.muted, marginTop: 6, maxWidth: 380,
          margin: "6px auto 0", lineHeight: 1.5,
        }}>
          {desc}
        </div>
      )}
    </div>
  );
}
